import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Phone, MapPin, ChevronDown, LogIn, LogOut, ListChecks, PackageOpen } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Logo } from "./Logo";
import { LOCALITIES } from "../data/services";
import { CONTACT } from "../data/content";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/#services" },
  { label: "Hire Pandit Ji", to: "/hire-pandit" },
  { label: "How It Works", to: "/#how-it-works" },
  { label: "Track Booking", to: "/track" },
  { label: "For Service Partners", to: "/partner-register" },
  { label: "About Us", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [locality, setLocality] = useState(LOCALITIES[0]);

  const AccountMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="header-account-menu"
          className="grid place-items-center w-9 h-9 rounded-full bg-navy text-white overflow-hidden ring-2 ring-transparent hover:ring-brand-orange transition-all"
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
          ) : (
            <span className="text-sm font-bold">{(user.name || user.email || "U").charAt(0).toUpperCase()}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <div className="px-3 py-2">
          <p data-testid="account-menu-name" className="text-sm font-semibold text-slate-900 truncate">{user.name || "Customer"}</p>
          <p data-testid="account-menu-email" className="text-xs text-slate-500 truncate">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="account-menu-bookings" onClick={() => navigate("/account/bookings")}>
          <PackageOpen className="w-4 h-4 mr-2 text-brand-orange" /> My Bookings
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="account-menu-track" onClick={() => navigate("/track")}>
          <ListChecks className="w-4 h-4 mr-2 text-brand-orange" /> Track Booking
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="account-logout-button" onClick={logout} className="text-red-600 focus:text-red-600">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/95 border-b border-slate-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Logo />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="header-location-select"
                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-navy transition-colors"
              >
                <MapPin className="w-4 h-4 text-brand-orange" />
                {locality.name}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {LOCALITIES.map((l) => (
                <DropdownMenuItem
                  key={l.id}
                  data-testid={`header-locality-${l.slug}`}
                  onClick={() => setLocality(l)}
                >
                  <MapPin className="w-4 h-4 mr-2 text-brand-orange" /> {l.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              data-testid={`header-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-slate-700 hover:text-brand-orange transition-colors whitespace-nowrap"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <AccountMenu />
          ) : (
            <Button
              variant="ghost"
              data-testid="header-signin-button"
              className="text-navy hover:bg-navy/5 font-semibold"
              onClick={login}
            >
              <LogIn className="w-4 h-4 mr-1.5" /> Sign in
            </Button>
          )}
          <Button
            variant="outline"
            data-testid="header-join-partner-button"
            className="border-navy/20 text-navy hover:bg-navy hover:text-white font-semibold"
            onClick={() => navigate("/partner-register")}
          >
            Join as a Partner
          </Button>
          <Button
            data-testid="header-book-cta-button"
            className="bg-brand-orange hover:bg-orange-600 text-white font-semibold shadow-sm active:scale-[0.98] transition-transform"
            onClick={() => navigate("/book")}
          >
            Book a Service
          </Button>
        </div>

        {/* Mobile */}
        <div className="flex md:hidden items-center gap-2">
          {user && <AccountMenu />}
          <a href={CONTACT.phoneHref} data-testid="header-mobile-call" className="grid place-items-center w-9 h-9 rounded-lg bg-slate-100 text-navy">            <Phone className="w-4 h-4" />
          </a>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="header-hamburger" className="grid place-items-center w-9 h-9 rounded-lg bg-navy text-white">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82vw] sm:w-80">
              <div className="mt-2 mb-6"><Logo /></div>
              <nav className="flex flex-col gap-1">
                {NAV.map((n) => (
                  <SheetClose asChild key={n.label}>
                    <Link
                      to={n.to}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="py-3 px-3 rounded-lg text-base font-medium text-slate-800 hover:bg-slate-100"
                    >
                      {n.label}
                    </Link>
                  </SheetClose>
                ))}
              </nav>
              <div className="mt-6 flex flex-col gap-3">
                {user ? (
                  <>
                    <SheetClose asChild>
                      <Button
                        variant="outline"
                        data-testid="mobile-mybookings-button"
                        className="w-full border-navy/20 text-navy font-semibold"
                        onClick={() => navigate("/account/bookings")}
                      >
                        <PackageOpen className="w-4 h-4 mr-1.5" /> My Bookings
                      </Button>
                    </SheetClose>
                    <SheetClose asChild>
                      <Button
                        variant="outline"
                        data-testid="mobile-logout-button"
                        className="w-full border-red-200 text-red-600 font-semibold"
                        onClick={logout}
                      >
                        <LogOut className="w-4 h-4 mr-1.5" /> Sign out ({user.name || user.email})
                      </Button>
                    </SheetClose>
                  </>
                ) : (
                  <SheetClose asChild>
                    <Button
                      variant="outline"
                      data-testid="mobile-signin-button"
                      className="w-full border-navy/20 text-navy font-semibold"
                      onClick={login}
                    >
                      <LogIn className="w-4 h-4 mr-1.5" /> Sign in with Google
                    </Button>
                  </SheetClose>
                )}
                <SheetClose asChild>
                  <Button
                    data-testid="mobile-book-cta"
                    className="w-full bg-brand-orange hover:bg-orange-600 text-white font-semibold"
                    onClick={() => navigate("/book")}
                  >
                    Book a Service
                  </Button>
                </SheetClose>
                <SheetClose asChild>
                  <Button
                    variant="outline"
                    data-testid="mobile-partner-cta"
                    className="w-full border-navy/20 text-navy font-semibold"
                    onClick={() => navigate("/partner-register")}
                  >
                    Join as a Partner
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
